import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { Activity, ArrowLeft, Check, ChevronRight, RefreshCw } from 'lucide-react';

type Trace = {
  id: string;
  name: string;
  tenant_id: string | null;
  user_id: string | null;
  session_id: string | null;
  feature: string;
  environment: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  coverage: string;
  retention_status: string;
  review_state: string;
  cost_usd?: string | null;
  cost_kind?: string;
  unknown_costs?: number;
};
type Span = {
  id: string;
  parent_span_id: string | null;
  name: string;
  type: string;
  model: string | null;
  started_at: string;
  ended_at: string | null;
  status: string;
  cost_kind: string;
  observed_cost_usd: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  span_metadata: Record<string, unknown>;
  price_snapshot: Record<string, unknown>;
};
type Evidence = {
  id: string;
  span_id: string;
  status: string;
  name: string;
  checksum: string;
  archived_at: string | null;
  created_at: string;
};
type Review = { id: string; rating: string; notes: string; author_id: string; created_at: string };
type Detail = Trace & {
  spans: Span[];
  events: Evidence[];
  reviews: Review[];
  metadata: Record<string, unknown>;
  replay_ids: string[];
  invocations?: Record<string, unknown>[];
  audit_events?: Record<string, unknown>[];
};
type Cost = {
  group: string | null;
  cost_kind: string;
  calls: number;
  usd: string | null;
  eur: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  missing_usage: number;
  latency_ms: string | null;
  error_rate: string | null;
};
const control = 'rounded-3xl border bg-background px-3 py-2 text-sm';
const panel = 'rounded-3xl border bg-card p-5';
const endpoint = '/cortex/traces/v1';
const label = (value: string) => value.replaceAll('_', ' ');
export const costText = (value: string | null | undefined, kind?: string) =>
  kind === 'subscription'
    ? 'Subscription'
    : value == null
      ? 'Unknown'
      : `$${Number(value).toFixed(6)}${kind === 'estimated' ? ' estimated' : ''}`;
const dateText = (value: string) => new Date(value).toLocaleString();
const duration = (start: string, end: string | null) =>
  end
    ? `${Math.max(0, new Date(end).getTime() - new Date(start).getTime()).toLocaleString()} ms`
    : 'Running';

function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-3xl bg-muted p-4 text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
function ErrorNotice({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="text-sm text-destructive">
      {error instanceof Error ? error.message : 'The request failed. Try again.'}
    </p>
  ) : null;
}

export function TracesPage() {
  const api = useApiClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const [group, setGroup] = useState('model');
  const [tab, setTab] = useState<'traces' | 'costs' | 'prices'>('traces');
  const search = useQuery({
    queryKey: ['ai-traces', filters, offset],
    queryFn: async () =>
      (
        await api.get<{ items: Trace[]; total: number }>(endpoint, {
          params: { ...filters, offset, limit: 25 },
        })
      ).data,
  });
  const costs = useQuery({
    queryKey: ['ai-costs', filters, group],
    queryFn: async () =>
      (
        await api.get<{ items: Cost[] }>(`${endpoint}/costs`, {
          params: { ...filters, group_by: group },
        })
      ).data,
    enabled: tab === 'costs',
  });
  const health = useQuery({
    queryKey: ['ai-evidence-health'],
    queryFn: async () =>
      (
        await api.get<{ pending: number; storage_failures: number; oldest_pending: string | null }>(
          `${endpoint}/health`
        )
      ).data,
    refetchInterval: 30_000,
  });
  if (selected) return <TraceDetail id={selected} onBack={() => setSelected(null)} />;
  return (
    <div className="space-y-6" data-testid="ai-traces-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">AI traces</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Inspect recorded AI work, review outcomes, and track observed costs.
          </p>
        </div>
        <button
          className={control}
          onClick={() => {
            void search.refetch();
            void costs.refetch();
            void health.refetch();
          }}
          aria-label="Refresh traces"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="AI inspection views">
        {(['traces', 'costs', 'prices'] as const).map(value => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={`${control} ${tab === value ? 'bg-primary text-primary-foreground' : ''}`}
            onClick={() => setTab(value)}
          >
            {value === 'prices' ? 'Pricing' : value === 'costs' ? 'Observed costs' : 'Traces'}
          </button>
        ))}
      </div>
      {health.data && (
        <p className="text-xs text-muted-foreground">
          {health.data.pending} payloads awaiting archival · {health.data.storage_failures} storage
          failures · Retention policy pending, automatic expiry disabled
          {health.data.oldest_pending
            ? ` · Oldest pending ${dateText(health.data.oldest_pending)}`
            : ''}
        </p>
      )}
      <ErrorNotice error={health.error} />
      {tab === 'prices' ? (
        <Pricing />
      ) : (
        <>
          <form
            className={`${panel} grid gap-3 sm:grid-cols-3 lg:grid-cols-4`}
            onSubmit={event => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              setFilters(
                Object.fromEntries(
                  [...data.entries()]
                    .filter(([, value]) => value !== '')
                    .map(([key, value]) => [
                      key,
                      key.startsWith('started_')
                        ? new Date(String(value)).toISOString()
                        : String(value),
                    ])
                )
              );
              setOffset(0);
            }}
          >
            {['tenant_id', 'user_id', 'feature', 'model', 'tool', 'environment'].map(name => (
              <label key={name} className="grid gap-1 text-xs">
                {label(name)}
                <input className={control} name={name} defaultValue={filters[name] ?? ''} />
              </label>
            ))}
            <label className="grid gap-1 text-xs">
              Status
              <select
                className={control}
                name="status"
                aria-label="Status"
                defaultValue={filters.status ?? ''}
              >
                <option value="">All statuses</option>
                {['running', 'completed', 'failed', 'cancelled'].map(value => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs">
              Review state
              <select
                className={control}
                name="review_state"
                aria-label="Review state"
                defaultValue={filters.review_state ?? ''}
              >
                <option value="">All reviews</option>
                {['unreviewed', 'pass', 'fail', 'needs_investigation'].map(value => (
                  <option key={value} value={value}>
                    {label(value)}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs">
              From
              <input className={control} type="datetime-local" name="started_after" />
            </label>
            <label className="grid gap-1 text-xs">
              Until
              <input className={control} type="datetime-local" name="started_before" />
            </label>
            <label className="grid gap-1 text-xs">
              Minimum USD
              <input className={control} name="min_cost" type="number" min="0" step="any" />
            </label>
            <label className="grid gap-1 text-xs">
              Maximum USD
              <input className={control} name="max_cost" type="number" min="0" step="any" />
            </label>
            <label className="grid gap-1 text-xs">
              Cost source
              <select className={control} name="cost_kind" aria-label="Cost source">
                <option value="">All sources</option>
                {['reported', 'estimated', 'unknown', 'subscription'].map(value => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <button
              className={`${control} self-end bg-primary text-primary-foreground`}
              type="submit"
            >
              Apply filters
            </button>
          </form>
          {tab === 'traces' ? (
            <div className={panel}>
              <ErrorNotice error={search.error} />
              {search.isPending ? (
                <p>Loading traces…</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="text-xs text-muted-foreground">
                        <tr>
                          {['Trace', 'Started', 'Status', 'Cost USD', 'Review', 'Capture'].map(
                            value => (
                              <th className="p-3" key={value}>
                                {value}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {search.data?.items.map(trace => (
                          <tr key={trace.id} className="border-t">
                            <td className="p-3">
                              <button
                                className="flex items-center gap-2 text-left font-medium hover:underline"
                                onClick={() => setSelected(trace.id)}
                              >
                                <Activity className="h-4 w-4" />
                                {trace.name}
                                <ChevronRight className="h-3 w-3" />
                              </button>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {trace.environment} · {trace.feature}
                              </p>
                            </td>
                            <td className="p-3">{dateText(trace.started_at)}</td>
                            <td className="p-3">{trace.status}</td>
                            <td className="p-3">
                              {costText(trace.cost_usd, trace.cost_kind)}
                              {Boolean(trace.unknown_costs) && trace.cost_usd != null && (
                                <p className="text-xs">+ {trace.unknown_costs} unknown</p>
                              )}
                            </td>
                            <td className="p-3">{label(trace.review_state)}</td>
                            <td className="p-3 text-xs">{label(trace.coverage)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {search.data?.items.length === 0 && (
                    <p className="py-10 text-center text-muted-foreground">
                      No traces match these filters.
                    </p>
                  )}
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span>{search.data?.total ?? 0} traces</span>
                    <div className="flex gap-2">
                      <button
                        className={control}
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - 25))}
                      >
                        Previous
                      </button>
                      <button
                        className={control}
                        disabled={offset + 25 >= (search.data?.total ?? 0)}
                        onClick={() => setOffset(offset + 25)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={`${panel} space-y-4`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Observed costs do not change spending limits. Missing usage and prices remain
                  unknown.
                </p>
                <select
                  aria-label="Group costs by"
                  className={control}
                  value={group}
                  onChange={event => setGroup(event.target.value)}
                >
                  {['model', 'feature', 'tenant', 'user', 'day'].map(value => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </div>
              <ErrorNotice error={costs.error} />
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      {[
                        'Group',
                        'Source',
                        'Calls',
                        'USD',
                        'Tokens in / out',
                        'Missing usage',
                        'Mean latency',
                        'Error rate',
                      ].map(value => (
                        <th className="p-2" key={value}>
                          {value}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {costs.data?.items.map((row, index) => (
                      <tr className="border-t" key={index}>
                        <td className="p-2">{row.group ?? 'Unattributed'}</td>
                        <td className="p-2">{row.cost_kind}</td>
                        <td className="p-2">{row.calls}</td>
                        <td className="p-2">{costText(row.usd, row.cost_kind)}</td>
                        <td className="p-2">
                          {row.input_tokens ?? '?'} / {row.output_tokens ?? '?'}
                        </td>
                        <td className="p-2">{row.missing_usage}</td>
                        <td className="p-2">
                          {row.latency_ms == null
                            ? 'Unknown'
                            : `${Number(row.latency_ms).toFixed(0)} ms`}
                        </td>
                        <td className="p-2">
                          {row.error_rate == null
                            ? 'Unknown'
                            : `${(Number(row.error_rate) * 100).toFixed(1)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {costs.isPending && <p>Loading costs…</p>}
              {costs.data?.items.length === 0 && <p>No costs match these filters.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TraceDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const api = useApiClient();
  const cache = useQueryClient();
  const [spanId, setSpanId] = useState<string | null>(null);
  const [payloadId, setPayloadId] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['ai-trace', id],
    queryFn: async () => (await api.get<Detail>(`${endpoint}/${id}`)).data,
  });
  const payload = useQuery({
    queryKey: ['ai-payload', id, payloadId],
    queryFn: async () => (await api.get<unknown>(`${endpoint}/${id}/payloads/${payloadId}`)).data,
    enabled: !!payloadId,
    gcTime: 0,
    staleTime: 0,
  });
  const review = useMutation({
    mutationFn: async (body: { rating: string; notes: string }) =>
      api.post(`${endpoint}/${id}/reviews`, body),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['ai-trace', id] });
      await cache.invalidateQueries({ queryKey: ['ai-traces'] });
    },
  });
  const trace = detail.data;
  const historical = useQuery({
    queryKey: ['ai-legacy-span', id, spanId],
    queryFn: async () => (await api.get(`${endpoint}/${id}/spans/${spanId}`)).data,
    enabled: !!spanId && trace?.coverage === 'historical',
    gcTime: 0,
  });
  const span = trace?.spans.find(item => item.id === spanId);
  const start = trace ? new Date(trace.started_at).getTime() : 0;
  const end = Math.max(
    start + 1,
    ...(trace?.spans.map(item => new Date(item.ended_at ?? item.started_at).getTime()) ?? [])
  );
  function depth(item: Span): number {
    let count = 0;
    const seen = new Set([item.id]);
    let parent = item.parent_span_id ?? String(item.span_metadata.parent_span_id ?? '');
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      count++;
      const found = trace?.spans.find(candidate => candidate.id === parent);
      parent = found?.parent_span_id ?? '';
    }
    return Math.min(count, 8);
  }
  return (
    <div className="space-y-5">
      <button className={`${control} flex items-center gap-2`} onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        All traces
      </button>
      <ErrorNotice error={detail.error} />
      {!trace ? (
        <p>Loading trace…</p>
      ) : (
        <>
          <div>
            <h1 className="text-2xl font-semibold">{trace.name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {trace.status} · {duration(trace.started_at, trace.ended_at)} · {trace.environment} ·{' '}
              {label(trace.coverage)}
            </p>
            <p className="mt-1 break-all text-xs text-muted-foreground">
              Trace {trace.id} · Tenant {trace.tenant_id ?? 'System'} · User{' '}
              {trace.user_id ?? 'System'}
            </p>
          </div>
          {trace.coverage !== 'recorded' && (
            <p className={`${panel} text-sm`}>
              This trace has incomplete capture. Historical records and older runners may lack
              prompts, usage, or tool relationships.
            </p>
          )}
          <section className={`${panel} space-y-3`} aria-label="Trace waterfall">
            <h2 className="font-semibold">Trace tree and waterfall</h2>
            <p className="text-xs text-muted-foreground">
              Bars share one time scale. Parallel work overlaps horizontally.
            </p>
            {[...trace.spans]
              .sort((a, b) => a.started_at.localeCompare(b.started_at))
              .map(item => {
                const left = Math.max(
                  0,
                  ((new Date(item.started_at).getTime() - start) / (end - start)) * 100
                );
                const width = Math.max(
                  0.5,
                  ((new Date(item.ended_at ?? item.started_at).getTime() -
                    new Date(item.started_at).getTime()) /
                    (end - start)) *
                    100
                );
                return (
                  <button
                    key={item.id}
                    className={`grid w-full grid-cols-2 items-center gap-3 rounded-3xl p-2 text-left text-sm ${spanId === item.id ? 'bg-accent' : 'hover:bg-muted'}`}
                    onClick={() => {
                      setSpanId(item.id);
                      setPayloadId(null);
                    }}
                  >
                    <span style={{ paddingLeft: depth(item) * 12 }}>
                      <span className="mr-2 text-xs text-muted-foreground">{item.type}</span>
                      {item.name}
                      <span className="ml-2 text-xs">{item.status}</span>
                    </span>
                    <span className="relative h-6 rounded-3xl bg-muted">
                      <span
                        className={`absolute top-1 h-4 rounded-3xl ${item.status === 'failed' ? 'bg-destructive' : 'bg-primary/60'}`}
                        style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
                      />
                    </span>
                  </button>
                );
              })}
            {trace.spans.length === 0 && <p>No spans were recorded.</p>}
          </section>
          {span && (
            <section className={`${panel} space-y-3`}>
              <h2 className="font-semibold">{span.name}</h2>
              <p className="text-sm">
                {span.model ?? 'No model'} · {duration(span.started_at, span.ended_at)} ·{' '}
                {costText(span.observed_cost_usd, span.cost_kind)} · Tokens{' '}
                {span.tokens_input ?? '?'} in / {span.tokens_output ?? '?'} out
              </p>
              <details>
                <summary className="cursor-pointer text-sm">
                  Context versions, correlation, and applied prices
                </summary>
                <Json value={{ context: span.span_metadata, price: span.price_snapshot }} />
              </details>
            </section>
          )}
          <section className={`${panel} space-y-3`}>
            <h2 className="font-semibold">Recorded conversation and payloads</h2>
            {historical.data !== undefined && <Json value={historical.data} />}
            <ErrorNotice error={historical.error} />
            <p className="text-xs text-muted-foreground">
              Content access is audited. Select a span to filter its records.
            </p>
            <div className="flex max-h-64 flex-wrap gap-2 overflow-auto">
              {trace.events
                .filter(item => !spanId || item.span_id === spanId)
                .map(item => (
                  <button
                    key={item.id}
                    data-testid={`payload-${item.id}`}
                    className={control}
                    onClick={() => setPayloadId(item.id)}
                  >
                    {item.name} · {item.status} · {dateText(item.created_at)} ·{' '}
                    {item.archived_at ? 'Archived' : 'Awaiting archive'}
                  </button>
                ))}
            </div>
            {payloadId && payload.isPending && <p>Loading recorded content…</p>}
            <ErrorNotice error={payload.error} />
            {payloadId && payload.data !== undefined && <Json value={payload.data} />}
          </section>
          <section className={`${panel} space-y-4`}>
            <h2 className="font-semibold">Manual review</h2>
            <form
              className="grid gap-3"
              onSubmit={event => {
                event.preventDefault();
                const data = new FormData(event.currentTarget);
                review.mutate({
                  rating: String(data.get('rating')),
                  notes: String(data.get('notes')),
                });
              }}
            >
              <label className="grid gap-1 text-sm">
                Rating
                <select
                  name="rating"
                  aria-label="Rating"
                  className={control}
                  defaultValue="needs_investigation"
                >
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                  <option value="needs_investigation">Needs investigation</option>
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Review notes
                <textarea name="notes" maxLength={20000} className={control} rows={3} />
              </label>
              <button
                disabled={review.isPending}
                className={`${control} flex w-fit items-center gap-2 bg-primary text-primary-foreground`}
              >
                <Check className="h-4 w-4" />
                Save review revision
              </button>
              <ErrorNotice error={review.error} />
              {review.isSuccess && (
                <p role="status" className="text-sm">
                  Review saved.
                </p>
              )}
            </form>
            {trace.reviews.map(item => (
              <div key={item.id} className="border-t pt-3 text-sm">
                <p>
                  {label(item.rating)} · {dateText(item.created_at)} · {item.author_id}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{item.notes}</p>
              </div>
            ))}
          </section>
          <section className={`${panel} space-y-3`}>
            <h2 className="font-semibold">Related records</h2>
            <p className="text-sm">Conversation {trace.session_id ?? 'None'}</p>
            <Json
              value={{
                references: trace.metadata,
                invocations: trace.invocations,
                audit_events: trace.audit_events,
              }}
            />
            {trace.replay_ids.map(replay => (
              <a
                key={replay}
                className="block text-sm underline"
                href={`/replays?session=${replay}`}
              >
                Session replay {replay}
              </a>
            ))}
            <p className="text-xs text-muted-foreground">
              Retention policy pending. Evidence has no automatic expiry. Clinical audit records
              remain authoritative.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

function Pricing() {
  const api = useApiClient();
  const cache = useQueryClient();
  const prices = useQuery({
    queryKey: ['ai-prices'],
    queryFn: async () => (await api.get<Record<string, unknown>[]>(`${endpoint}/prices`)).data,
  });
  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => api.post(`${endpoint}/prices`, body),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['ai-prices'] }),
  });
  return (
    <section className={`${panel} space-y-4`}>
      <h2 className="font-semibold">Effective-dated model prices</h2>
      <p className="text-sm text-muted-foreground">
        USD per million tokens. New windows close the previous open window. Previously recorded
        costs retain their applied prices.
      </p>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={event => {
          event.preventDefault();
          const data = Object.fromEntries(new FormData(event.currentTarget));
          save.mutate({
            ...data,
            effective_from: new Date(String(data.effective_from)).toISOString(),
          });
        }}
      >
        {[
          'provider',
          'model',
          'effective_from',
          'input_per_million_usd',
          'output_per_million_usd',
          'cache_read_per_million_usd',
          'cache_creation_per_million_usd',
        ].map(name => (
          <label className="grid gap-1 text-xs" key={name}>
            {label(name)}
            <input
              required
              name={name}
              className={control}
              type={
                name === 'effective_from'
                  ? 'datetime-local'
                  : name.endsWith('_usd')
                    ? 'number'
                    : 'text'
              }
              min="0"
              step="0.0001"
            />
          </label>
        ))}
        <button
          disabled={save.isPending}
          className={`${control} self-end bg-primary text-primary-foreground`}
        >
          Save price window
        </button>
      </form>
      <ErrorNotice error={save.error ?? prices.error} />
      {save.isSuccess && <p role="status">Price window saved.</p>}
      <Json value={prices.data ?? []} />
    </section>
  );
}
